<template>
  <div class="video-player-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="el-icon-loading"></i>
      </div>
      <div class="loading-text">正在加载视频...</div>
    </div>

    <!-- 视频播放器 -->
    <div v-else-if="videoData" class="video-container">
      <div class="video-player-wrapper">
        <video
          ref="videoPlayer"
          :src="videoData.videoUrl"
          controls
          autoplay
          class="video-player"
          @loadstart="onVideoLoadStart"
          @loadeddata="onVideoLoaded"
          @error="onVideoError"
        >
          您的浏览器不支持视频播放
        </video>
      </div>

      <!-- 视频信息 -->
      <div class="video-info">
        <!-- 操作按钮 -->
        <div class="video-actions">
          <el-button type="primary" @click="downloadVideo">
            <i class="el-icon-download"></i>
            下载视频
          </el-button>
          <el-button @click="goBack">
            <i class="el-icon-back"></i>
            返回
          </el-button>
        </div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else class="error-container">
      <div class="error-icon">
        <i class="el-icon-warning-outline"></i>
      </div>
      <div class="error-text">视频加载失败</div>
      <el-button type="primary" @click="retry">重试</el-button>
    </div>
  </div>
</template>

<script>
import { getVideoDetailApi } from '@/apis/human'

export default {
  name: 'VideoPlayer',
  data() {
    return {
      loading: true,
      videoData: null,
      orderNo: '',
      isPlaying: false,
      isMuted: false,
      currentTime: '00:00',
      duration: '00:00'
    }
  },
  async mounted() {
    this.orderNo = this.$route.query.orderNo;
    if (this.orderNo) {
      await this.loadVideoData();
    } else {
      this.loading = false;
    }
  },
  methods: {
    async loadVideoData() {
      try {
        this.loading = true;
        const response = await getVideoDetailApi({ orderNo: this.orderNo });
        
        if (response && response.code === 0) {
          this.videoData = response.data;
        } else {
          this.$message.error(response.msg || '获取视频信息失败');
        }
      } catch (error) {
        console.error('加载视频数据失败:', error);
        this.$message.error('加载视频数据失败');
      } finally {
        this.loading = false;
      }
    },
    
    togglePlay() {
      const video = this.$refs.videoPlayer;
      if (video.paused) {
        video.play();
        this.isPlaying = true;
      } else {
        video.pause();
        this.isPlaying = false;
      }
    },
    
    toggleMute() {
      const video = this.$refs.videoPlayer;
      video.muted = !video.muted;
      this.isMuted = video.muted;
    },
    
    toggleFullscreen() {
      const video = this.$refs.videoPlayer;
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      } else if (video.mozRequestFullScreen) {
        video.mozRequestFullScreen();
      }
    },
    
    onVideoLoadStart() {
      console.log('视频开始加载');
    },
    
    onVideoLoaded() {
      const video = this.$refs.videoPlayer;
      this.duration = this.formatTime(video.duration);
      
      video.addEventListener('timeupdate', () => {
        this.currentTime = this.formatTime(video.currentTime);
      });
      
      video.addEventListener('play', () => {
        this.isPlaying = true;
      });
      
      video.addEventListener('pause', () => {
        this.isPlaying = false;
      });
    },
    
    onVideoError() {
      this.$message.error('视频播放出错');
    },
    
    formatTime(seconds) {
      if (isNaN(seconds)) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN');
    },
    
    getStatusText(status) {
      const statusMap = {
        30: '已完成',
        'success': '已完成',
        'completed': '已完成',
        'processing': '处理中',
        'failed': '失败'
      };
      return statusMap[status] || '未知';
    },
    
    async downloadVideo() {
      if (!this.videoData || !this.videoData.videoUrl) {
        this.$message.error('视频地址不存在');
        return;
      }

      try {

        // 使用fetch获取视频文件
        const response = await fetch(this.videoData.videoUrl);
        if (!response.ok) {
          throw new Error('下载失败');
        }

        // 获取文件blob
        const blob = await response.blob();

        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.videoData.title || '数字人视频'}.mp4`;

        // 触发下载
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 清理URL对象
        window.URL.revokeObjectURL(url);

        this.$message.success('视频下载完成');
      } catch (error) {
        console.error('下载失败:', error);
        this.$message.error('视频下载失败，请重试');

        // 如果fetch失败，尝试直接打开链接
        window.open(this.videoData.videoUrl, '_blank');
      }
    },
    
    goBack() {
      window.close();
    },
    
    retry() {
      this.loadVideoData();
    }
  }
}
</script>

<style lang="scss" scoped>
.video-player-page {
  min-height: 100vh;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.loading-container, .error-container {
  text-align: center;
  color: white;
  
  .loading-spinner, .error-icon {
    font-size: 48px;
    margin-bottom: 20px;
  }
  
  .loading-text, .error-text {
    font-size: 18px;
    margin-bottom: 20px;
  }
}

.video-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
}

.video-player-wrapper {
  position: relative;
  background: #f5f5f5;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  width: 320px;
  height: 568px;

  .video-player {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #f5f5f5;
  }
  
}

.video-info {
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  width: 320px;
  backdrop-filter: blur(10px);

  .video-title {
    font-size: 18px;
    font-weight: 600;
    color: white;
    margin-bottom: 12px;
    text-align: center;
  }

  .video-meta {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;

    .meta-item {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.8);

      i {
        color: #7848f1;
      }
    }
  }

  .video-actions {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;

    .el-button {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      padding: 8px 12px;
      width: 45%;
      justify-content: center;

      &.el-button--primary {
        background: #7848f1;
        border-color: #7848f1;
      }
    }
  }
}

@media (max-width: 768px) {
  .video-player-page {
    padding: 10px;
  }

  .video-container {
    max-width: 90vw;
  }

  .video-player-wrapper {
    width: 280px;
    height: 498px;
  }

  .video-info {
    width: 280px;
    padding: 16px;

    .video-title {
      font-size: 16px;
    }

    .video-actions {
      gap: 6px;

      .el-button {
        font-size: 11px;
        padding: 6px 10px;
      }
    }
  }
}
</style>
